/**
 * useUserData Hook
 * 사용자 관리 데이터 훅
 *
 * @description
 * - 사용자 목록 조회 (검색 필터 지원)
 * - 사용자 생성 (초기 비밀번호 자동 설정)
 * - 사용자 수정
 * - 권한 코드 목록 조회
 * - useRef를 사용한 중복 API 호출 방지
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { userApi } from '../api/userApi';
import {
    User,
    userListFromDto,
    CreateUserRequestDto,
    UpdateUserRequestDto
} from '../types/user.types';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { commonCodeApi } from '@/shared/api/commonCodeApi';
import { CommonCodeDto } from '@/shared/types/commonCode.types';

// ======== 메시지 상수 ========

const TOAST_MESSAGES = {
    USER_LIST_ERROR: '사용자 목록을 불러오는데 실패했습니다.',
    USER_CREATE_SUCCESS: '사용자가 생성되었습니다.',
    USER_CREATE_ERROR: '사용자 생성에 실패했습니다.',
    USER_UPDATE_SUCCESS: '사용자 정보가 수정되었습니다.',
    USER_UPDATE_ERROR: '사용자 수정에 실패했습니다.',
} as const;

/**
 * 사용자 관리 데이터 훅
 * @returns {Object} 사용자 관련 상태 및 함수
 * @property {boolean} loading - 로딩 상태
 * @property {User[]} users - 사용자 목록
 * @property {CommonCodeDto[]} prmCodes - 권한 코드 목록
 * @property {Function} fetchUsers - 사용자 목록 조회 함수
 * @property {Function} createUser - 사용자 생성 함수
 * @property {Function} updateUser - 사용자 수정 함수
 * @property {Function} refreshUsers - 사용자 목록 새로고침 함수
 * @property {Function} initializeUsers - 초기 데이터 로드 함수
 */
export const useUserData = () => {
    const { showToast } = useToast();

    // ======== 상태 관리 ========

    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [prmCodes, setPrmCodes] = useState<CommonCodeDto[]>([]);
    const hasInitialized = useRef(false);
    const hasFetchedPrmCodes = useRef(false);

    // ======== API 핸들러 ========

    /**
     * 사용자 목록 조회
     * @param params 검색 파라미터
     */
    const fetchUsers = useCallback(async (params?: {
        userId?: string;
        userName?: string;
        prmCode?: string;
        useYn?: string;
    }) => {
        try {
            setLoading(true);
            const response = await userApi.getUserList(params);
            setUsers(response ? userListFromDto(response) : []);
        } catch {
            showToast(TOAST_MESSAGES.USER_LIST_ERROR, { type: ToastType.ERROR });
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    /**
     * 사용자 생성
     * @param request 생성 요청 데이터
     */
    const createUser = useCallback(async (request: CreateUserRequestDto) => {
        try {
            setLoading(true);
            
            // 공통코드에서 초기 비밀번호 조회
            const passwordResponse = await commonCodeApi.getCommonCodeList('INIT_PASSWORD');
            const initialPassword = passwordResponse.codes?.[0]?.val1;
            
            if (!initialPassword) {
                showToast('초기 비밀번호 설정이 없습니다. 공통코드를 확인해주세요.', { type: ToastType.ERROR });
                return null;
            }
            
            // 요청에 초기 비밀번호 추가
            const requestWithPassword = {
                ...request,
                password: initialPassword
            };
            
            const response = await userApi.createUser(requestWithPassword);
            if (response.success) {
                showToast(TOAST_MESSAGES.USER_CREATE_SUCCESS, { type: ToastType.SUCCESS });
                await fetchUsers();
                return response;
            }
            showToast(response.message || TOAST_MESSAGES.USER_CREATE_ERROR, { type: ToastType.ERROR });
            return null;
        } catch {
            showToast(TOAST_MESSAGES.USER_CREATE_ERROR, { type: ToastType.ERROR });
            return null;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchUsers]);

    /**
     * 사용자 수정
     * @param userId 사용자 ID
     * @param request 수정 요청 데이터
     */
    const updateUser = useCallback(async (userId: string, request: UpdateUserRequestDto) => {
        try {
            setLoading(true);
            const response = await userApi.updateUser(userId, request);
            if (response.success) {
                showToast(TOAST_MESSAGES.USER_UPDATE_SUCCESS, { type: ToastType.SUCCESS });
                await fetchUsers();
                return response;
            }
            showToast(response.message || TOAST_MESSAGES.USER_UPDATE_ERROR, { type: ToastType.ERROR });
            return null;
        } catch {
            showToast(TOAST_MESSAGES.USER_UPDATE_ERROR, { type: ToastType.ERROR });
            return null;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchUsers]);

    // ======== 권한 코드 로드 ========

    /**
     * 권한 코드 목록 조회 (마운트 시 1회)
     */
    useEffect(() => {
        if (hasFetchedPrmCodes.current) return;

        let isCancelled = false;

        const fetchPrmCodes = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('PRM');
                if (!isCancelled) {
                    setPrmCodes(response.codes || []);
                    hasFetchedPrmCodes.current = true;
                }
            } catch {
                // 권한 코드 조회 실패 시 조용히 처리
            }
        };

        fetchPrmCodes();
        return () => { isCancelled = true; };
    }, []);

    // 초기 데이터 로드 - 파라미터를 받을 수 있도록 수정
    const initializeUsers = useCallback((params?: {
        userId?: string;
        userName?: string;
        prmCode?: string;
        useYn?: string;
    }) => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;
            fetchUsers(params).catch(() => {
                // 에러는 훅에서 처리
            });
        }
    }, [fetchUsers]);

    return {
        loading,
        users,
        prmCodes,
        fetchUsers,
        createUser,
        updateUser,
        refreshUsers: fetchUsers,
        initializeUsers
    };
};
